var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import styles from '@cof/fs-alert-core/lib/alert.lit.min.js';
import { css, html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
/**
 * The web component implementation of the Financial Services alert component
 *
 * ## Installation
 *
 * ```bash
 * npm i @cof/fs-alert-wc
 * ```
 *
 * ## Usage
 *
 * To import the side-effectful registration of `<fs-alert>` call
 *
 * ```javascript
 * import '@cof/fs-alert-wc/fs-alert.js';
 * ```
 *
 * To import the `FsAlert` class as either a base class or to self-register the custom element use
 *
 * ```javascript
 * import { FsAlert } from '@cof/fs-alert-wc';
 * ```
 *
 * ## Example
 *
 * ```html
 * <fs-alert type="informational" @close="${closeCallback}" @request-action="${actionCallback}" action-button="Learn more">
 *   Alert message lorem ipsum dolor sit amet.
 * </fs-alert>
 * ```
 *
 * @element fs-alert
 * @fires close - Fires when the alert is closing, if preventDefault is not called the alert will close
 * @fires request-action - Fires when the alert's action button is clicked
 * @slot - The alert content
 */
export class FsAlert extends LitElement {
    constructor() {
        super(...arguments);
        /** If present the action button will be displayed */
        this.actionButton = '';
        /** The style of alert to display to the user */
        this.type = 'informational';
    }
    static get styles() {
        return [
            css `
      :host {
        display: block;
      }
    `,
            styles
        ];
    }
    render() {
        return html `<div role="alert" class="fs-alert fs-alert--${this.type}">
      <slot></slot>
      ${this.actionButton && this.type !== 'global'
            ? html `<button type="button" class="fs-alert__button" @click="${this._requestAction}">${this.actionButton}</button>`
            : ''}
      ${this.type === 'global'
            ? html `<button type="button" class="fs-alert__close" aria-label="Close alert" @click="${this._requestClose}"></button>`
            : ''}
    </div>`;
    }
    /** Add accessibility attributes */
    firstUpdated() {
        this.setAttribute('role', 'alert');
        this.setAttribute('aria-atomic', 'true');
    }
    /** Remove the alert from the DOM */
    close() {
        this.remove();
    }
    /** Alert the consuming application the user is requesting some action */
    _requestAction() {
        const event = new CustomEvent('request-action', {});
        this.dispatchEvent(event);
    }
    /**
     * Request to close the alert. There might be some occasion where the
     * consuming application wishes not to let the alert be closed. In that case
     * a close event is fired. If the event is not defaultPrevented, the close
     * method will be called.
     */
    _requestClose() {
        const closeEvent = new CustomEvent('close', { cancelable: true });
        this.dispatchEvent(closeEvent);
        if (!closeEvent.defaultPrevented) {
            this.close();
        }
    }
}
/** The element's canonical name for use with safe define */
FsAlert.canonicalName = 'alert';
__decorate([
    property({ type: String, attribute: 'action-button' })
], FsAlert.prototype, "actionButton", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsAlert.prototype, "type", void 0);
//# sourceMappingURL=Alert.js.map